<template>
  <DialogWrapper
    closeOnCancel
    class="talent-group-add-dialog"
    hasCustomTitle
    :show.sync="show"
    :loading="loading"
    @save="handleSave"
  >
    <template #title>
      <div class="header">
        <div class="title">{{ $t(`${i18nPrefix}.title.${isEditor ? 'edit' : 'add'}`) }}</div>
        <div class="private-switch">
          <span class="label">{{ $t(`${i18nPrefix}.label.private`) }}</span>
          <el-switch
            v-if="refreshSwitch"
            v-model="groupInfo.is_private"
            active-color="#0096cc"
            :active-text="$t('common.yes')"
            :inactive-text="$t('common.no')"
            :width="switchWidth"
            @change="parentGroupChange"
          ></el-switch>
        </div>
      </div>
    </template>
    <div class="content">
      <WrappedForm
        class="add-talent-group-form"
        ref="form"
        hideRequiredAsterisk
        :model="groupInfo"
        :formLines="formLines"
      >
      <template #parentGroupLabel>
        <div class="parent-group-label">
          <span>{{ $t(`${i18nPrefix}.label.parentGroup`) }}</span>
          <span class="sub-label">{{ $t(`${i18nPrefix}.label.parentGroupTip`) }}</span>
        </div>
      </template>
      <template #access>
        <div class="has-assign-badge">
          <div class="assign-count-badge">
            <span class="count">{{ accountCount }}</span>
            <span class="label">{{ getCountLabel(accountCount, 'admin.talent.label.recruiter') }}</span>
          </div>
          <div class="assign-count-badge">
            <span class="count">{{ roleCount }}</span>
            <span class="label">{{ getCountLabel(roleCount, 'admin.talent.label.role') }}</span>
          </div>
        </div>
      </template>
      <template #parentGroupSelector>
        <el-select v-model="groupInfo.parentGroup" clearable filterable>
          <el-option
            v-for="(item, index) in parentVisibleGroupList"
            :key="index"
            :label="item.name"
            :value="item.id"
            :style="item.style"
          />
        </el-select>
      </template>
      <template #recruiter>
        <Assign
          labelI18nKey="admin.talent.label.recruiterAccess"
          :showLabel="false"
          :selectIds.sync="currentOwnerInfo.account_ids"
          :optionList="accountList"
        ></Assign>
      </template>
      <template #role>
        <Assign
          labelI18nKey="admin.talent.label.roleAccess"
          :showLabel="false"
          :selectIds.sync="currentOwnerInfo.role_ids"
          :optionList="roleList"
        ></Assign>
      </template>
      </WrappedForm>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import Assign from '../../talentPool/components/assign.vue'

export default {
  components: {
    DialogWrapper,
    WrappedForm,
    Assign
  },
  props: {
    isEditor: {
      type: Boolean,
      default: false
    },
    currentGroupFilter: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.talent',
      refreshSwitch: true,
      show: false,
      loading: false,
      parentGroupList: [],
      parentGroupByVisibilityList: [],
      groupInfo: {
        is_private: false
      },
      accountList: [],
      roleList: [],
      currentOwnerInfo: {
        account_ids: [],
        role_ids: []
      }
    }
  },
  computed: {
    accountCount () {
      return this.currentOwnerInfo?.account_ids?.length || 0
    },
    roleCount () {
      return this.currentOwnerInfo?.role_ids?.length || 0
    },
    showBadge () {
      return this.accountCount > 0 || this.roleCount > 0
    },
    switchWidth () {
      if (this.$i18n.locale === 'en') {
        return 55
      } else {
        return 50
      }
    },
    hasAssignGroupPermission () {
      if (!this.isEditor) {
        return true
      }
      return this.currentGroupFilter.current_user_can_edit
    },
    parentVisibleGroupList () {
      const list = _.filter(this.parentGroupByVisibilityList, item => item.parents_ids.length < 2).filter(item => item.id !== _.parseInt(this.currentGroupFilter.id))
      const parentGroupList = []
      const children = _.groupBy(list, 'parent_id')
      _.forEach(list, item => {
        if (!item.parent_id) {
          parentGroupList.push(item)
          children[item.id] = _.map(children[item.id], child => {
            return {
              ...child,
              style: {
                'padding-left': '30px'
              }
            }
          })
          parentGroupList.push(...children[item.id])
        }
      })

      return parentGroupList
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        groupName: {
          label: this.$t(`${this.i18nPrefix}.label.groupName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        parentGroup: {
          label: this.$t(`${this.i18nPrefix}.label.parentGroup`),
          labelSlot: 'parentGroupLabel',
          contentSlot: 'parentGroupSelector'
        },
        access: {
          hidden: !this.groupInfo.is_private || !this.showBadge,
          label: this.$t(`${this.i18nPrefix}.label.access`),
          contentSlot: 'access'
        },
        accountIds: {
          hidden: !this.groupInfo.is_private || !this.hasAssignGroupPermission,
          label: this.$t('admin.talent.label.recruiterAccess'),
          contentSlot: 'recruiter'
        },
        roleIds: {
          hidden: !this.groupInfo.is_private || !this.hasAssignGroupPermission,
          label: this.$t('admin.talent.label.roleAccess'),
          contentSlot: 'role'
        }
      }
      return lines
    }
  },
  created () {
  },
  methods: {
    ...mapActions('talent', ['getTalentFilterParentGroup']),
    ...mapActions('company', ['getTeamMemberList']),
    ...mapActions('roleManagement', ['getRoleList']),
    ...mapGetters('account', ['getCurrentAccount']),
    async initPage () {
      this.loading = true
      await Promise.all([
        this.loadAccountList(),
        this.loadRoleList(),
        this.loadTalentFilterParentGroup()
      ])
      this.loading = false
    },
    async loadTalentFilterParentGroup () {
      const resp = await this.getTalentFilterParentGroup()
      if (resp) {
        this.parentGroupList = resp
        if (this.isEditor) {
          this.parentGroupByVisibilityList = this.parentGroupList
        } else {
          this.parentGroupByVisibilityList = _.filter(this.parentGroupList, group => group.is_public)
        }
      }
    },
    async loadAccountList () {
      const account = this.getCurrentAccount()
      const params = {
        companyId: account.root_company_id,
        is_all: true,
        exclude_disabled: true
      }
      const resp = await this.getTeamMemberList(params)
      if (resp) {
        this.accountList = _.orderBy(resp.results || [], [item => (item.name || '').toLowerCase()], ['asc'])
      }
    },
    async loadRoleList () {
      const resp = await this.getRoleList()
      if (resp) {
        this.roleList = _.map(resp.role_list || [], role => ({
          id: role.id,
          name: role.title
        }))
      }
    },
    parentGroupChange () {
      this.$set(this.groupInfo, 'parentGroup', null)
      if (this.groupInfo.is_private) {
        this.parentGroupByVisibilityList = _.filter(this.parentGroupList, group => !group.is_public)
      } else {
        this.parentGroupByVisibilityList = _.filter(this.parentGroupList, group => group.is_public)
      }
    },
    showDialog () {
      this.show = true
    },
    closeDialog () {
      this.show = false
    },
    clearForm () {
      this.groupInfo = {}
      this.$nextTick(() => {
        this.$refs.form.clearValidate()
      })
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      const isValid = await this.$refs.form.submit()
      if (!isValid) {
        return
      }
      this.loading = true
      this.groupInfo.is_public = !this.groupInfo.is_private
      this.groupInfo.ownerInfo = {
        account_ids: this.currentOwnerInfo.account_ids,
        role_ids: this.currentOwnerInfo.role_ids
      }
      if (this.isEditor) {
        this.$emit('update', this.groupInfo, close => {
          this.loading = false
          if (close) {
            this.closeDialog()
            this.loadTalentFilterParentGroup()
          }
        })
      } else {
        this.$emit('save', this.groupInfo, close => {
          this.loading = false
          if (close) {
            this.closeDialog()
            this.loadTalentFilterParentGroup()
          }
        })
      }
    },
    getCountLabel (count, i18nKey) {
      const label = this.$t(i18nKey)
      if (this.$i18n.locale === 'en' && count > 1) {
        return `${label}s`
      }
      return label
    }
  },
  watch: {
    show () {
      if (this.show) {
        this.clearForm()
        this.initPage()
        this.currentOwnerInfo.account_ids = []
        this.currentOwnerInfo.role_ids = []
      }
      if (this.isEditor) {
        this.currentOwnerInfo.account_ids = this.currentGroupFilter?.owner_info?.account_ids || []
        this.currentOwnerInfo.role_ids = this.currentGroupFilter?.owner_info?.role_ids || []
        this.$set(this.groupInfo, 'groupName', this.currentGroupFilter.group_name)
        this.$set(this.groupInfo, 'is_private', !this.currentGroupFilter.is_public)
        this.$set(this.groupInfo, 'parentGroup', this.currentGroupFilter.parent_group)
        this.$set(this.groupInfo, 'accountIds', this.currentOwnerInfo.account_ids)
        this.$set(this.groupInfo, 'roleIds', this.currentOwnerInfo.role_ids)
      }
    },
    switchWidth (newValue, oldValue) {
      this.refreshSwitch = false
      this.$nextTick(() => {
        this.refreshSwitch = true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.talent-group-add-dialog {
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
    .title {
      font-size: 22px;
      font-weight: 700;
      color: #303133;
      line-height: 24px;
    }

    .private-switch {
      display: flex;
      align-items: center;
      .label {
        margin-right: 12px;
        font-size: 16px;
      }
      .el-switch {
        ::v-deep {
          .el-switch__label {
            display: none;
            position: absolute;
            height: 100%;
            color: #ffffff;
            z-index: 1;
            margin: 0;
            &.is-active {
              display: inline-block;
            }
          }
          .el-switch__label--left {
            left: 25px;
          }
          .el-switch__core {
            width: 55px;
          }
          .el-switch__label--right {
            left: 10px;
          }
        }
      }
    }
  }
  .content {
    .add-talent-group-form {
      .has-assign-badge {
        color: #0096cc;
        font-size: 13px;
        display: inline-flex;
        align-items: center;
        gap: 8px;

        .assign-count-badge {
          .count {
            margin-right: 4px;
          }
        }
      }
      .el-form-item {

      }

      .parent-group-label {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: center;
        line-height: 16px;

        .sub-label {
          margin-top: 2px;
          color: #909399;
          font-size: 12px;
          line-height: 14px;
        }
      }
    }
  }
}
</style>
